import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/LoadingSpinner'
import ImageUploader from '../components/ImageUploader'
import { MEDIA_BASE_URL } from '../components/Gallery'
import './FlightLogEditor.css'

const GALLERY_RE = /\{gallery\}[^{}]*\{\/gallery\}/i

function FlightLogEditor() {
  const { logId } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuth()
  const isEdit = Boolean(logId)

  const [sites, setSites] = useState([])
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [galleryFolder, setGalleryFolder] = useState(null)
  const [uploadedImages, setUploadedImages] = useState([]) // {name,url}
  // "Other" lets pilots log a non-Wingbeat site by typing a free-text name.
  const [otherSite, setOtherSite] = useState(false)
  // Set after a successful submit that resulted in a pending (unpublished) log.
  const [submitted, setSubmitted] = useState(null)

  const [form, setForm] = useState({
    title: '',
    flight_date: new Date().toISOString().slice(0, 10),
    site_name: '',
    site_id: '',
    content: '',
    featured_image: '',
  })

  // Load site options
  useEffect(() => {
    api.sites.getAllSites().then((s) => setSites(s || [])).catch(() => {})
  }, [])

  // Load existing log for edit
  useEffect(() => {
    if (!isEdit) return
    let active = true
    api.flightLogs.getFlightLog(logId)
      .then((log) => {
        if (!active || !log) return
        setForm({
          title: log.title || '',
          flight_date: (log.flight_date || log.created_date || '').slice(0, 10),
          site_name: log.site_name || '',
          site_id: log.site_id || '',
          content: log.content || log.introtext || '',
          featured_image: log.featured_image || '',
        })
        // A saved site_name with no site_id means it was a free-text "Other" site.
        if (log.site_name && !log.site_id) setOtherSite(true)
      })
      .catch((e) => setError(e.message))
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [isEdit, logId])

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }))

  const onSiteChange = (e) => {
    const value = e.target.value
    if (value === '__other__') {
      // Switch to free-text entry; clear any selected site_id.
      setOtherSite(true)
      setForm((f) => ({ ...f, site_id: '', site_name: '' }))
      return
    }
    setOtherSite(false)
    const site = sites.find((s) => s.site_id === value)
    setForm((f) => ({ ...f, site_id: value, site_name: site ? site.name : '' }))
  }

  const onOtherSiteChange = (e) => {
    // Free-text site: no site_id, name is whatever the pilot types.
    setForm((f) => ({ ...f, site_id: '', site_name: e.target.value }))
  }

  // After images upload, remember the folder + previews and ensure the content
  // contains the {gallery} shortcode so the images render in the article.
  const handleUploaded = (folder, filenames) => {
    setGalleryFolder(folder)
    setUploadedImages((prev) => [
      ...prev,
      ...filenames.map((name) => ({
        name,
        url: `${MEDIA_BASE_URL}/images/${folder}/thumbs/${encodeURIComponent(name)}`,
        full: `images/${folder}/${name}`,
      })),
    ])
    setForm((f) => {
      if (GALLERY_RE.test(f.content)) return f // already has one
      const shortcode = `\n\n{gallery}${folder}{/gallery}`
      return { ...f, content: (f.content || '') + shortcode }
    })
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    if (!form.title.trim()) { setError('Please enter a title.'); return }
    if (!form.content.trim()) { setError('Please enter the flight write-up.'); return }

    setSaving(true)
    try {
      // Default the featured image to the first uploaded photo if the pilot
      // didn't pick one, so cards/detail hero have an image.
      const featured = form.featured_image || uploadedImages[0]?.full || null
      const payload = {
        title: form.title.trim(),
        content: form.content,
        flight_date: form.flight_date || null,
        site_name: form.site_name,
        site_id: form.site_id,
        featured_image: featured,
        gallery_folder: galleryFolder || undefined,
        author_name: user?.name || user?.username || undefined,
      }
      const res = isEdit
        ? await api.flightLogs.updateFlightLog(logId, payload)
        : await api.flightLogs.createFlightLog(payload)
      const saved = res?.data || {}
      const newId = saved.log_id || logId

      // If the log is published (admins publish immediately), go straight to it.
      // Otherwise it's PENDING_APPROVAL - the public view would 404, so show a
      // confirmation instead of redirecting to a page the user can't see yet.
      if (saved.status === 'PUBLISHED') {
        navigate(newId ? `/flight-logs/${newId}` : '/flight-logs')
      } else {
        setSubmitted({ pending: true, message: res?.message })
      }
    } catch (err) {
      setError(err.message || 'Could not save the flight log.')
      setSaving(false)
    }
  }

  if (!isAuthenticated) {
    return (
      <div className="page-container">
        <div className="page-header"><h1>Members only</h1></div>
        <div className="fle-form"><p>Please <a href="/login">log in</a> to add a flight log.</p></div>
      </div>
    )
  }

  // Post-submit confirmation for a pending (unpublished) log. The public log
  // page isn't viewable until an admin approves it, so we confirm here rather
  // than redirect to a "Not Found" page.
  if (submitted?.pending) {
    return (
      <div className="page-container">
        <div className="page-header"><h1>Thanks — flight log submitted</h1></div>
        <div className="fle-form">
          <p>{submitted.message || 'Your flight log has been submitted and is awaiting approval by an administrator. It will appear publicly once approved.'}</p>
          <p>You can view your own submissions (including pending ones) from the flight logs page.</p>
          <div className="fle-actions">
            <button type="button" className="btn btn-primary" onClick={() => navigate('/flight-logs')}>
              Back to flight logs
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return <div className="page-container"><LoadingSpinner size="large" message="Loading…" /></div>
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>{isEdit ? 'Edit flight log' : 'New flight log'}</h1>
      </div>

      <form className="fle-form" onSubmit={onSubmit}>
        {error && <div className="fle-error" role="alert">{error}</div>}

        <label className="fle-row">
          <span>Title</span>
          <input name="title" type="text" value={form.title} onChange={update} required />
        </label>

        <div className="fle-two">
          <label className="fle-row">
            <span>Flight date</span>
            <input name="flight_date" type="date" value={form.flight_date} onChange={update} />
          </label>
          <label className="fle-row">
            <span>Site</span>
            <select name="site_id" value={otherSite ? '__other__' : form.site_id} onChange={onSiteChange}>
              <option value="">— select a site —</option>
              {sites.map((s) => (
                <option key={s.site_id} value={s.site_id}>{s.name}</option>
              ))}
              <option value="__other__">Other (not a Wingbeat site)…</option>
            </select>
            {otherSite && (
              <input
                type="text"
                className="fle-other-site"
                value={form.site_name}
                onChange={onOtherSiteChange}
                placeholder="Enter the site name"
                aria-label="Other site name"
                style={{ marginTop: '0.5rem' }}
              />
            )}
          </label>
        </div>

        <label className="fle-row">
          <span>Write-up</span>
          <textarea name="content" rows={12} value={form.content} onChange={update} required
            placeholder="How was the flight? Conditions, launch, the flying…" />
          <small className="fle-hint">
            Basic HTML is allowed. A {'{gallery}'} tag is added automatically when you upload photos.
          </small>
        </label>

        <div className="fle-row">
          <span>Photos</span>
          <ImageUploader date={form.flight_date} site={form.site_name || form.site_id} onUploaded={handleUploaded} />
        </div>

        {uploadedImages.length > 0 && (
          <div className="fle-row">
            <span>Featured image (optional)</span>
            <div className="fle-featured-pick">
              <label className={`fle-featured-opt ${!form.featured_image ? 'sel' : ''}`}>
                <input type="radio" name="featured_image" value="" checked={!form.featured_image} onChange={update} />
                None
              </label>
              {uploadedImages.map((img) => (
                <label key={img.name} className={`fle-featured-opt ${form.featured_image === img.full ? 'sel' : ''}`}>
                  <input type="radio" name="featured_image" value={img.full}
                    checked={form.featured_image === img.full} onChange={update} />
                  <img src={img.url} alt={img.name} />
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="fle-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Submit flight log'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
        </div>
      </form>
    </div>
  )
}

export default FlightLogEditor
